<template>
  <div class="page-wrap">

    <!-- Results shown after successful upload -->
    <template v-if="store.hasData">

      <!-- Summary bar -->
      <div class="summary-bar">
        <div class="stat-card">
          <div class="label">ลูกค้าทั้งหมด</div>
          <div class="value" style="color:#1d4ed8">{{ store.totalCustomers }}</div>
        </div>
        <div v-for="t in tierOptions" :key="t" class="stat-card">
          <div class="label">{{ TIER_DISPLAY[t] }}</div>
          <div class="value" :style="{ color: TIER_COLOR[t] }">{{ tierCounts[t] }}</div>
        </div>
      </div>

      <!-- Legend -->
      <div class="legend-card">
        <strong>วิธีอ่านคะแนน</strong> — คะแนนทุกตัวอยู่ในช่วง 0–100 ยิ่งสูงยิ่งดี
        <div class="legend-grid">
          <div class="legend-item">🛡️ <span><strong>ความน่าเชื่อถือ (Risk)</strong> — 100 = ปลอดภัยสุด, 0 = เสี่ยงสุด</span></div>
          <div class="legend-item">💎 <span><strong>มูลค่า (Value)</strong> — 100 = ลูกค้ามูลค่าสูงสุด</span></div>
          <div class="legend-item">📊 <span><strong>DMI</strong> — ความสมบูรณ์ของข้อมูล: น้อย = ดึงคะแนนเข้าใกล้ 50</span></div>
          <div class="legend-item">📈 <span><strong>Trend Adj</strong> — การปรับจากแนวโน้ม: <span style="color:#166534">+บวก = ดีขึ้น</span>, <span style="color:#991b1b">-ลบ = แย่ลง</span></span></div>
        </div>
      </div>

      <!-- Results header -->
      <div class="results-header">
        <h2>ผลการวิเคราะห์ — {{ store.totalCustomers }} ลูกค้า</h2>
        <div style="display:flex;gap:8px">
          <a class="btn-dashboard" href="#" @click.prevent="router.push({ name: 'CsDashboard' })">📊 ดูภาพรวม Dashboard →</a>
          <a class="btn-dashboard" href="#"    @click.prevent="router.push({ name: 'CsTop100' })">🏆 รายงาน TOP 100 →</a>
        </div>
      </div>

      <!-- Table -->
      <div class="table-wrap">
        <div class="filter-bar">
          <input v-model="search" class="filter-input" type="text" placeholder="🔍 ค้นหาชื่อลูกค้า…" />
          <select v-model="tierFilter" class="filter-select">
            <option value="">— Tier ทั้งหมด —</option>
            <option v-for="t in tierOptions" :key="t" :value="t">{{ TIER_DISPLAY[t] }}</option>
          </select>
          <button class="filter-clear" @click="clearFilters">ล้าง</button>
          <span v-if="search || tierFilter" class="filter-result">
            แสดง {{ Math.min(displayedCount, store.isAllLoaded ? filteredResults.length : store.totalCount) }} /
            {{ store.isAllLoaded ? filteredResults.length : store.totalCount }} รายการที่กรอง
            (ทั้งหมด {{ store.totalCustomers }} ลูกค้า)
          </span>
          <span v-else class="filter-result">
            แสดง {{ Math.min(displayedCount, store.totalCustomers) }} / {{ store.totalCustomers }} ลูกค้า
          </span>
        </div>

        <div ref="tableScrollRef" class="table-scroll">
        <table>
          <thead>
            <tr>
              <th class="col-rank">#</th>
              <th class="th-sort" @click="setSort('customerName')">
                ชื่อลูกค้า<span class="sort-icon">{{ sortIcon('customerName') }}</span>
              </th>
              <th class="th-sort" @click="setSort('tier')">
                Tier<span class="sort-icon">{{ sortIcon('tier') }}</span>
              </th>
              <th class="th-sort" @click="setSort('riskFinal')">
                ความน่าเชื่อถือ<span class="sort-icon">{{ sortIcon('riskFinal') }}</span>
                <span class="col-sub">Risk Score ↑ สูง = ปลอดภัย</span>
              </th>
              <th>Trend (Risk)<span class="col-sub">Trend / ต่อดอก / Early Warning</span></th>
              <th class="th-sort" @click="setSort('valueFinal')">
                มูลค่า<span class="sort-icon">{{ sortIcon('valueFinal') }}</span>
                <span class="col-sub">Value Score ↑ สูง = ดี</span>
              </th>
              <th>Trend (Value)<span class="col-sub">แนวโน้มมูลค่า</span></th>
              <th class="th-sort" @click="setSort('dmi')">
                DMI<span class="sort-icon">{{ sortIcon('dmi') }}</span>
                <span class="col-sub">ความสมบูรณ์ข้อมูล</span>
              </th>
              <th>ข้อมูล<span class="col-sub">เปิด / ปิด / ธุรกรรม</span></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(r, i) in displayedItems" :key="r.customerId || r.customerName">
              <td class="col-rank">{{ i + 1 }}</td>

              <!-- Customer name -->
              <td style="max-width:220px" :title="r.customerName">
                <a class="cust-link" href="#" @click.prevent="store.selectedCustomerId = customerRouteId(r); router.push({ name: 'CsPortfolio' })">{{ r.customerName }}</a>
              </td>

              <!-- Tier -->
              <td><span :class="'tier-badge tier-' + r.tierKey">{{ r.tier }}</span></td>

              <!-- Risk score -->
              <td>
                <div class="score-cell">
                  <div class="score-main">
                    <div class="score-bar"><div class="score-fill score-risk" :style="{ width: r.riskFinal + '%' }"></div></div>
                    <span class="score-num">{{ fmtNum(r.riskFinal, 2) }}</span>
                  </div>
                  <div class="score-base">
                    <span>ฐาน {{ fmtNum(r.riskRaw, 2) }}</span>
                    <span class="score-arrow">→</span>
                    <span>DMI {{ fmtNum(r.riskAnchored != null ? r.riskAnchored : r.riskRaw, 2) }}</span>
                  </div>
                </div>
              </td>

              <!-- Risk trend -->
              <td>
                <div v-if="hasRiskTrend(r)" class="adj-row">
                  <span v-if="r.riskTrendAdj !== 0"        :class="'adj-badge ' + adjCls(r.riskTrendAdj)">Trend {{ signedNum(r.riskTrendAdj, 1) }}</span>
                  <span v-if="r.lateRenewalTrendAdj !== 0" :class="'adj-badge ' + adjCls(r.lateRenewalTrendAdj)">ต่อดอก {{ signedNum(r.lateRenewalTrendAdj, 1) }}</span>
                  <span v-if="r.earlyWarningAdj !== 0"     :class="'adj-badge ' + adjCls(r.earlyWarningAdj)">⚠ Warning {{ signedNum(r.earlyWarningAdj, 1) }}</span>
                </div>
                <span v-else class="adj-none">—</span>
              </td>

              <!-- Value score -->
              <td>
                <div class="score-cell">
                  <div class="score-main">
                    <div class="score-bar"><div class="score-fill score-value" :style="{ width: r.valueFinal + '%' }"></div></div>
                    <span class="score-num">{{ fmtNum(r.valueFinal, 2) }}</span>
                  </div>
                  <div class="score-base">
                    <span>ฐาน {{ fmtNum(r.valueRaw, 2) }}</span>
                    <span class="score-arrow">→</span>
                    <span>DMI {{ fmtNum(r.valueAnchored != null ? r.valueAnchored : r.valueRaw, 2) }}</span>
                  </div>
                </div>
              </td>

              <!-- Value trend -->
              <td>
                <div v-if="r.valueTrendAdj !== 0" class="adj-row">
                  <span :class="'adj-badge ' + adjCls(r.valueTrendAdj)">Trend {{ signedNum(r.valueTrendAdj, 1) }}</span>
                </div>
                <span v-else class="adj-none">—</span>
              </td>

              <!-- DMI -->
              <td>
                <div class="dmi-cell">
                  <span class="dmi-num">{{ fmtNum(r.dmi, 2) }}</span>
                  <div class="dmi-bar"><div class="dmi-fill" :style="{ width: r.dmi + '%' }"></div></div>
                </div>
              </td>

              <!-- Data -->
              <td>
                <div class="data-cell">
                  <div class="data-row"><span class="data-dot dot-open"></span>{{ r.openChains }} เปิดอยู่ (฿{{ fmtBaht(r.openPrincipal) }})</div>
                  <div v-if="r.postponedChains > 0" class="data-row"><span class="data-dot dot-postponed"></span>{{ r.postponedChains }} ผลัด</div>
                  <div class="data-row"><span class="data-dot dot-closed"></span>{{ r.redeemedChains }} ปิดแล้ว</div>
                  <div v-if="r.forfeitedChains > 0" class="data-row"><span class="data-dot dot-forfeited"></span>{{ r.forfeitedChains }} หลุด</div>
                  <div class="data-row"><span class="data-dot dot-tx"></span>{{ r.totalTransactions }} ธุรกรรม</div>
                  <template v-if="r.overdueChains > 0">
                    <div class="data-row" style="margin-top:3px">
                      <span class="overdue-badge">⚠ ค้าง {{ r.overdueChains }} ตั๋ว</span>
                    </div>
                    <div class="overdue-detail">฿{{ fmtBaht(r.overduePrincipal) }} · {{ r.maxDaysOverdue }} วัน</div>
                  </template>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- sentinel อยู่ใน scroll container เพื่อให้ IntersectionObserver จับได้ -->
        <div ref="sentinelRef" style="height:1px"></div>

        <div v-if="loadingMore || store.loadingMore || store.loading" class="load-more-row">
          <div class="spinner"></div>
          <span>{{ store.loading ? 'กำลังค้นหา…' : 'กำลังโหลดเพิ่มเติม…' }}</span>
        </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
/* eslint-disable no-unused-vars */
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useScoreStore } from '@/stores/score.js'
import { scoreApi, currentScope, hasScope } from '@/utils/scoreSvcApiNew.js'

const router = useRouter()
const store  = useScoreStore()

onMounted(() => { store.init(); fetchStatus() })

const uploadOpen       = ref(false)
const selectedFile     = ref(null)
const selectedFileName = ref('')
const fileInputRef     = ref(null)
const uploading        = ref(false)
const isDragging       = ref(false)
const fileError        = ref('')

// ── Upload mode (full replace vs incremental delta) ───────────
const mode                 = ref('full')          // 'full' | 'delta'
const statusLoaded         = ref(false)
const hasExistingData      = ref(false)
const existingCustomerCount = ref(0)
const lastScoreDate        = ref(null)
const previewData          = ref(null)            // result of /upload-delta/preview
const showReplaceConfirm   = ref(false)

async function fetchStatus() {
  try {
    // Admin app: scope comes from the selected shop and rides in the POST body (see scoreSvcApi).
    const s = await scoreApi.status()
    hasExistingData.value       = !!s.hasData
    existingCustomerCount.value = s.customerCount || 0
    lastScoreDate.value         = s.lastScoreDate || null
    // Sensible default: brand-new system → full; existing data → delta
    mode.value = hasExistingData.value ? 'delta' : 'full'
  } catch (_) {
    /* status is advisory only — backend still enforces guards */
  } finally {
    statusLoaded.value = true
  }
}

function setMode(m) {
  if (mode.value === m) return
  mode.value = m
  previewData.value = null
  fileError.value = ''
}

const canSubmit = computed(() => {
  if (!selectedFile.value || uploading.value) return false
  if (mode.value === 'delta' && statusLoaded.value && !hasExistingData.value) return false
  return true
})

const primaryLabel = computed(() => {
  if (mode.value === 'delta') return 'วิเคราะห์ส่วนต่าง'
  return hasExistingData.value ? 'ลบทั้งหมดแล้ววิเคราะห์ใหม่' : 'วิเคราะห์คะแนน'
})

function handlePrimary() {
  if (!canSubmit.value) return
  if (mode.value === 'delta') { runPreview(); return }
  // full mode
  if (hasExistingData.value) { showReplaceConfirm.value = true; return }
  doFullUpload()
}

function confirmFullReplace() {
  showReplaceConfirm.value = false
  doFullUpload()
}

function cancelPreview() {
  previewData.value = null
}
const search           = ref('')
const tierFilter       = ref('')
const loadingMore      = ref(false)
const displayedCount   = ref(50)
const sentinelRef      = ref(null)
const tableScrollRef   = ref(null)
let   observer         = null

// ── Tier constants ────────────────────────────────────────────
const TIER_ORDER = ['Platinum','Gold','Silver','Watch','Preferred','Standard','Manage','Guard','Caution']
const TIER_DISPLAY = {
  Platinum: 'Platinum', Gold: 'Gold', Silver: 'Silver',
  Watch: 'ติดตาม', Preferred: 'ปานกลาง', Standard: 'ทั่วไป',
  Manage: 'ติดตามใกล้ชิด', Guard: 'เฝ้าระวัง', Caution: 'ระวัง',
}
const TIER_COLOR = {
  Platinum: '#6d28d9', Gold: '#92400e', Silver: '#475569',
  Watch: '#1d4ed8',    Preferred: '#075985', Standard: '#1e40af',
  Manage: '#b91c1c',   Guard: '#92400e',     Caution: '#991b1b',
}

// ── Tier summary ──────────────────────────────────────────────
// Use server-computed distribution when available; fall back to results only immediately after upload.
const tierCounts  = computed(() => {
  if (store.summary?.tierDistribution) return store.summary.tierDistribution
  const c = {}
  store.results.forEach(r => { c[r.tierKey] = (c[r.tierKey] || 0) + 1 })
  return c
})
const tierOptions = computed(() => TIER_ORDER.filter(t => tierCounts.value[t] > 0))

// ── Sort state ────────────────────────────────────────────────
const sortKey = ref('riskFinal')
const sortDir = ref('desc')

function setSort(key) {
  if (sortKey.value === key) {
    sortDir.value = sortDir.value === 'desc' ? 'asc' : 'desc'
  } else {
    sortKey.value = key
    sortDir.value = (key === 'customerName' || key === 'tier') ? 'asc' : 'desc'
  }
  displayedCount.value = 50
}
function sortIcon(key) {
  if (sortKey.value !== key) return ' ⇅'
  return sortDir.value === 'desc' ? ' ↓' : ' ↑'
}

// ── Filter + display pagination ───────────────────────────────
// When all data is in memory (post-upload or small dataset), filter client-side.
// Otherwise the server already filtered via ?search=&tier= params.
const filteredResults = computed(() => {
  if (!store.isAllLoaded) return store.results
  const name = search.value.toLowerCase().trim()
  const tier = tierFilter.value
  return store.results.filter(r =>
    (!name || [r.customerName, r.cusUid, r.customerId].some(v => String(v || '').toLowerCase().includes(name))) &&
    (!tier || r.tierKey === tier)
  )
})
const sortedResults = computed(() => {
  const arr = [...filteredResults.value]
  const dir = sortDir.value === 'asc' ? 1 : -1
  arr.sort((a, b) => {
    if (sortKey.value === 'customerName') {
      return dir * String(a.customerName || '').localeCompare(String(b.customerName || ''), 'th')
    }
    if (sortKey.value === 'tier') {
      return dir * (TIER_ORDER.indexOf(a.tierKey) - TIER_ORDER.indexOf(b.tierKey))
    }
    return dir * ((a[sortKey.value] || 0) - (b[sortKey.value] || 0))
  })
  return arr
})
const displayedItems = computed(() =>
  sortedResults.value.slice(0, displayedCount.value)
)
let searchTimeout = null
let suppressFilterWatch = false
function clearFilters() {
  suppressFilterWatch = true
  search.value = ''
  tierFilter.value = ''
  displayedCount.value = 50
  clearTimeout(searchTimeout)
  void store.setFilter('', '')
  nextTick(() => { suppressFilterWatch = false })
}
watch([search, tierFilter], () => {
  if (suppressFilterWatch) return
  displayedCount.value = 50
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(async () => {
    await store.setFilter(search.value, tierFilter.value)
  }, 300)
})

// ── Format helpers ────────────────────────────────────────────
function fmtNum(n, dec = 0) {
  return Number(n).toLocaleString('th-TH', { minimumFractionDigits: dec, maximumFractionDigits: dec })
}
function fmtBaht(n) {
  const v = Number(n)
  if (v >= 1000000) return (v / 1000000).toFixed(1) + 'M'
  if (v >= 1000)    return (v / 1000).toFixed(1) + 'K'
  return v.toLocaleString('th-TH', { maximumFractionDigits: 0 })
}
function signedNum(v, dec) {
  const n = Number(v)
  return (n > 0 ? '+' : '') + fmtNum(n, dec)
}
function hasRiskTrend(r) {
  return r.riskTrendAdj !== 0 || r.lateRenewalTrendAdj !== 0 || r.earlyWarningAdj !== 0
}
function adjCls(v) { return v > 0 ? 'adj-pos' : v < 0 ? 'adj-neg' : 'adj-zero' }
function customerRouteId(r) { return store.routeCustomerId(r) }

// ── Scroll-to-reveal inside table container ───────────────────
async function loadMore() {
  if (loadingMore.value || store.loadingMore) return
  // Server has more pages — fetch next before revealing more rows
  if (store.hasMore) {
    const loaded = await store.loadNextPage()
    if (!loaded) return
  }
  const active = sortedResults.value
  if (displayedCount.value >= active.length) return
  loadingMore.value = true
  requestAnimationFrame(() => {
    displayedCount.value += 50
    loadingMore.value = false
  })
}

watch(sentinelRef, (el) => {
  if (observer) { observer.disconnect(); observer = null }
  if (!el) return
  observer = new IntersectionObserver(
    (entries) => { if (entries[0].isIntersecting) loadMore() },
    { root: tableScrollRef.value, rootMargin: '200px', threshold: 0 }
  )
  observer.observe(el)
})

onUnmounted(() => {
  if (observer) observer.disconnect()
  store.clearFilterState()
})

// ── File handling ─────────────────────────────────────────────
function onFileChange(e) {
  const f = e.target.files[0]
  if (f) { selectedFile.value = f; selectedFileName.value = f.name + ' (' + (f.size / 1024).toFixed(1) + ' KB)'; fileError.value = ''; previewData.value = null }
}
function onDrop(e) {
  isDragging.value = false
  const f = e.dataTransfer.files[0]
  if (f && f.name.endsWith('.xlsx')) {
    selectedFile.value = f; selectedFileName.value = f.name + ' (' + (f.size / 1024).toFixed(1) + ' KB)'; fileError.value = ''; previewData.value = null
  } else {
    fileError.value = 'กรุณาใช้ไฟล์ .xlsx เท่านั้น'
  }
}

// ── Upload ────────────────────────────────────────────────────
// Admin app: shop must be selected so groupUid/shopUid can be attached.
// Returns the parsed shop, or null after setting fileError.
function requireShop() {
  if (!hasScope()) {
    fileError.value = 'กรุณา Switch Shop ก่อน Upload เพื่อระบุสาขาที่ต้องการ'
    return null
  }
  return currentScope()
}

// Full replace upload
async function doFullUpload() {
  if (!selectedFile.value) return
  const shopData = requireShop()
  if (!shopData) return
  uploading.value = true
  fileError.value = ''
  previewData.value = null
  try {
    const data = await scoreApi.upload(selectedFile.value, shopData)
    displayedCount.value = 50
    store.saveResults(data, new Date().toISOString().slice(0, 10))
    await afterUploadSuccess()
  } catch (err) {
    fileError.value = 'เกิดข้อผิดพลาด: ' + err.message
  } finally {
    uploading.value = false
  }
}

// Delta step 1 — dry-run preview (no DB write)
async function runPreview() {
  if (!selectedFile.value) return
  const shopData = requireShop()
  if (!shopData) return
  uploading.value = true
  fileError.value = ''
  previewData.value = null
  try {
    previewData.value = await scoreApi.uploadDeltaPreview(selectedFile.value, shopData)
  } catch (err) {
    fileError.value = 'เกิดข้อผิดพลาด: ' + err.message
  } finally {
    uploading.value = false
  }
}

// Delta step 2 — commit (re-sends the same file)
async function confirmDeltaCommit() {
  if (!selectedFile.value) return
  const shopData = requireShop()
  if (!shopData) return
  uploading.value = true
  fileError.value = ''
  try {
    const data = await scoreApi.uploadDelta(selectedFile.value, shopData)
    displayedCount.value = 50
    store.saveResults(data.results, new Date().toISOString().slice(0, 10))
    previewData.value = null
    await afterUploadSuccess()
  } catch (err) {
    fileError.value = 'เกิดข้อผิดพลาด: ' + err.message
  } finally {
    uploading.value = false
  }
}

async function afterUploadSuccess() {
  selectedFile.value = null
  selectedFileName.value = ''
  await fetchStatus()
  await nextTick()
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

// (readErrorMessage lived here — ScoreSvcError already carries the server's message,
//  extracted the same way, so the local copy was dropped.)

</script>

<style scoped>
/* ── Page wrapper ────────────────────────────────────────────── */
.page-wrap {
  width: 100%;
  padding: 16px 24px;
  font-family: var(--bs-body-font-family, "THSarabunNew", "Sukhumvit Set", "Prompt", "Tahoma", "Roboto", sans-serif);
}
.page-wrap h2 { font-family: inherit; }

/* ── Upload card ─────────────────────────────────────────────── */
.upload-card {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(0,0,0,.08), 0 4px 16px rgba(0,0,0,.06);
  overflow: hidden;
  margin-bottom: 28px;
}
.upload-toggle {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
  color: #1a1d23;
  text-align: left;
  transition: background .15s;
}
.upload-toggle:hover { background: #f8f9fb; }
.toggle-chevron {
  font-size: 1.2rem;
  color: #9ca3af;
  transform: rotate(90deg);
  transition: transform .2s;
  line-height: 1;
}
.toggle-chevron.open { transform: rotate(270deg); }
.upload-body { padding: 0 24px 24px; }

/* ── Mode toggle ─────────────────────────────────────────────── */
.mode-toggle { display: flex; gap: 12px; margin-bottom: 16px; flex-wrap: wrap; }
.mode-btn {
  flex: 1; min-width: 220px; text-align: left; cursor: pointer;
  display: flex; flex-direction: column; gap: 3px;
  padding: 12px 16px; border: 2px solid #e2e6ea; border-radius: 8px;
  background: #fff; transition: border-color .15s, background .15s;
}
.mode-btn:hover { border-color: #c7d2fe; }
.mode-btn.active { border-color: #1d4ed8; background: #eef2ff; }
.mode-title { font-size: .92rem; font-weight: 700; color: #1a1d23; }
.mode-desc  { font-size: .76rem; color: #6b7280; }

/* ── Status / warn lines ─────────────────────────────────────── */
.status-line {
  font-size: .84rem; color: #374151; background: #f8faff;
  border: 1px solid #e2e6ea; border-radius: 7px; padding: 9px 14px; margin-bottom: 14px;
}
.status-line strong { color: #1d4ed8; }
.warn-box {
  font-size: .84rem; color: #92400e; background: #fffbeb;
  border: 1px solid #fde68a; border-radius: 7px; padding: 10px 14px; margin-bottom: 14px;
}

/* ── Cancel + danger buttons ─────────────────────────────────── */
.btn-upload.btn-danger { background: #b91c1c; }
.btn-upload.btn-danger:hover { background: #991b1b; }
.btn-cancel {
  margin-top: 20px; margin-left: 10px; padding: 11px 24px;
  background: #fff; color: #6b7280; border: 1px solid #d1d5db;
  border-radius: 7px; font-size: .95rem; font-weight: 600; cursor: pointer;
}
.btn-cancel:hover { border-color: #9ca3af; color: #1a1d23; }
.btn-cancel:disabled { opacity: .5; cursor: not-allowed; }

/* ── Delta preview box ───────────────────────────────────────── */
.preview-box {
  margin-top: 18px; padding: 16px 20px; border: 1px solid #c7d2fe;
  border-radius: 9px; background: #f8f9ff;
}
.preview-title { font-size: .9rem; font-weight: 700; color: #1a1d23; margin-bottom: 12px; }
.preview-grid { display: flex; gap: 14px; flex-wrap: wrap; }
.preview-stat {
  flex: 1; min-width: 96px; display: flex; flex-direction: column; align-items: center;
  gap: 2px; padding: 12px; background: #fff; border-radius: 8px; border: 1px solid #e2e6ea;
}
.ps-num   { font-size: 1.4rem; font-weight: 700; }
.ps-num.add { color: #166534; }
.ps-num.upd { color: #1d4ed8; }
.ps-num.dup { color: #9ca3af; }
.ps-num.tot { color: #1a1d23; }
.ps-label { font-size: .74rem; color: #6b7280; }
.preview-actions { display: flex; align-items: center; margin-top: 4px; }

/* ── Confirm modal ───────────────────────────────────────────── */
.modal-overlay {
  position: fixed; inset: 0; background: rgba(15,23,42,.5);
  display: flex; align-items: center; justify-content: center; z-index: 1000;
}
.modal-card {
  background: #fff; border-radius: 12px; padding: 24px 28px; max-width: 440px;
  width: calc(100% - 48px); box-shadow: 0 10px 40px rgba(0,0,0,.25);
}
.modal-title { font-size: 1.05rem; font-weight: 700; color: #b91c1c; margin-bottom: 12px; }
.modal-body  { font-size: .9rem; color: #374151; line-height: 1.6; margin-bottom: 20px; }
.modal-actions { display: flex; align-items: center; }
.modal-actions .btn-upload { margin-top: 0; }
.modal-actions .btn-cancel { margin-top: 0; }

/* ── Drop zone ───────────────────────────────────────────────── */
.drop-zone {
  border: 2px dashed #c7d2fe;
  border-radius: 8px;
  padding: 40px 24px;
  text-align: center;
  background: #f8f9ff;
  cursor: pointer;
  transition: border-color .2s, background .2s;
}
.drop-zone.drag-over { border-color: #1d4ed8; background: #eef2ff; }
.dz-icon  { font-size: 2.4rem; margin-bottom: 12px; }
.drop-zone p  { color: #6b7280; font-size: .9rem; }
.drop-zone strong { color: #1d4ed8; }
.file-name { margin-top: 10px; font-size: .85rem; color: #1d4ed8; font-weight: 600; }

/* ── Upload button ───────────────────────────────────────────── */
.btn-upload {
  margin-top: 20px;
  padding: 11px 28px;
  background: #1d4ed8;
  color: #fff;
  border: none;
  border-radius: 7px;
  font-size: .95rem;
  font-weight: 600;
  cursor: pointer;
  transition: background .15s, transform .1s;
}
.btn-upload:hover    { background: #1e40af; }
.btn-upload:active   { transform: scale(.98); }
.btn-upload:disabled { opacity: .5; cursor: not-allowed; }

/* ── Loading spinner ─────────────────────────────────────────── */
.loading { display: flex; align-items: center; gap: 10px; margin-top: 18px; color: #6b7280; font-size: .9rem; }
.spinner {
  width: 20px; height: 20px;
  border: 3px solid #c7d2fe;
  border-top-color: #1d4ed8;
  border-radius: 50%;
  animation: spin .7s linear infinite;
  flex-shrink: 0;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Error box ───────────────────────────────────────────────── */
.error-box {
  margin-top: 14px;
  padding: 12px 16px;
  background: #fef2f2;
  border: 1px solid #fca5a5;
  border-radius: 7px;
  color: #b91c1c;
  font-size: .88rem;
}

/* ── Summary bar ─────────────────────────────────────────────── */
.summary-bar { display: flex; gap: 14px; flex-wrap: wrap; margin-bottom: 20px; }
.stat-card {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(0,0,0,.08), 0 4px 16px rgba(0,0,0,.06);
  padding: 16px 22px;
  min-width: 120px; flex: 1;
}
.stat-card .label { font-size: .78rem; color: #6b7280; text-transform: uppercase; letter-spacing: .5px; }
.stat-card .value { font-size: 1.6rem; font-weight: 700; margin-top: 4px; }

/* ── Legend card ─────────────────────────────────────────────── */
.legend-card {
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 10px;
  padding: 14px 20px;
  margin-bottom: 20px;
  font-size: .83rem;
  color: #78350f;
  line-height: 1.6;
}
.legend-card strong { color: #92400e; }
.legend-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 32px; margin-top: 8px; }
.legend-item { display: flex; align-items: flex-start; gap: 6px; }

/* ── Results header ──────────────────────────────────────────── */
.results-header {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 14px; flex-wrap: wrap; gap: 8px;
}
.results-header h2 { font-size: 1.05rem; }
.btn-dashboard {
  padding: 8px 18px;
  background: #0f172a;
  color: #fff;
  text-decoration: none;
  border-radius: 7px;
  font-size: .85rem;
  font-weight: 600;
  transition: background .15s;
  display: inline-block;
}
.btn-dashboard:hover { background: #1e293b; }

/* ── Table wrap ──────────────────────────────────────────────── */
.table-wrap {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(0,0,0,.08), 0 4px 16px rgba(0,0,0,.06);
  overflow: hidden;
}
.table-scroll {
  overflow-y: auto;
  overflow-x: auto;
  max-height: calc(50 * 72px);
}
.filter-bar {
  display: flex; gap: 10px; align-items: center; flex-wrap: wrap;
  padding: 12px 16px; border-bottom: 1px solid #e2e6ea; background: #fafbff;
}
.filter-input {
  padding: 7px 12px; border: 1px solid #e2e6ea; border-radius: 6px;
  font-size: .84rem; outline: none; min-width: 220px; flex: 1; background: #fff;
}
.filter-input:focus { border-color: #1d4ed8; box-shadow: 0 0 0 2px rgba(29,78,216,.1); }
.filter-select { padding: 7px 12px; border: 1px solid #e2e6ea; border-radius: 6px; font-size: .84rem; outline: none; background: #fff; cursor: pointer; }
.filter-select:focus { border-color: #1d4ed8; }
.filter-clear  { padding: 7px 12px; font-size: .82rem; color: #6b7280; background: none; border: 1px solid #e2e6ea; border-radius: 6px; cursor: pointer; }
.filter-clear:hover { color: #1a1d23; border-color: #9ca3af; }
.filter-result { font-size: .78rem; color: #6b7280; white-space: nowrap; margin-left: auto; }

table { width: 100%; border-collapse: collapse; font-size: .85rem; line-height: 1.55; }
thead th {
  background: #f8faff; padding: 10px 12px; text-align: left;
  font-size: .74rem; font-weight: 700; color: #6b7280;
  text-transform: uppercase; letter-spacing: .4px;
  border-bottom: 2px solid #e2e6ea; white-space: nowrap;
  position: sticky; top: 0; z-index: 1;
}
.col-sub  { display: block; font-size: .68rem; font-weight: 400; color: #9ca3af; text-transform: none; letter-spacing: 0; margin-top: 1px; }
.col-rank { color: #6b7280; font-size: .8rem; width: 40px; text-align: center; }
.th-sort  { cursor: pointer; user-select: none; }
.th-sort:hover { background: #eef2ff; color: #1d4ed8; }
.sort-icon { color: #9ca3af; font-style: normal; }
tbody tr  { border-bottom: 1px solid #e2e6ea; transition: background .1s; }
tbody tr:last-child { border-bottom: none; }
tbody tr:hover { background: #fafbff; }
td { padding: 11px 12px; vertical-align: middle; line-height: 1.55; }

/* ── Customer name link ──────────────────────────────────────── */
.cust-link {
  color: #1d4ed8; text-decoration: none; font-weight: 600;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  display: block; max-width: 100%; line-height: 1.55; padding-block: 1px;
}
.cust-link:hover { text-decoration: underline; }

/* ── Tier badge ──────────────────────────────────────────────── */
.tier-badge   { display: inline-block; padding: 3px 10px; border-radius: 20px; font-size: .76rem; font-weight: 700; letter-spacing: .2px; white-space: nowrap; }
.tier-Platinum{ background: #ede9fe; color: #6d28d9; }
.tier-Gold    { background: #fef9c3; color: #92400e; }
.tier-Silver  { background: #f1f5f9; color: #475569; }
.tier-Watch   { background: #dbeafe; color: #1d4ed8; }
.tier-Preferred{ background: #e0f2fe; color: #075985; }
.tier-Standard{ background: #eff6ff; color: #1e40af; }
.tier-Manage  { background: #fee2e2; color: #b91c1c; }
.tier-Guard   { background: #fef3c7; color: #92400e; }
.tier-Caution { background: #fde8e8; color: #991b1b; }

/* ── Score cell ──────────────────────────────────────────────── */
.score-cell  { display: flex; flex-direction: column; gap: 3px; }
.score-main  { display: flex; align-items: center; gap: 7px; }
.score-bar   { width: 56px; height: 6px; background: #e5e7eb; border-radius: 4px; overflow: hidden; flex-shrink: 0; }
.score-fill  { height: 100%; border-radius: 4px; }
.score-risk  { background: linear-gradient(90deg, #ef4444 0%, #f59e0b 50%, #10b981 100%); }
.score-value { background: linear-gradient(90deg, #94a3b8 0%, #60a5fa 50%, #1d4ed8 100%); }
.score-num   { font-weight: 700; font-size: .92rem; min-width: 32px; }
.score-base  { font-size: .72rem; color: #6b7280; display: flex; align-items: center; gap: 4px; padding-left: 2px; }
.score-arrow { color: #d1d5db; }

/* ── Adjustment badges ───────────────────────────────────────── */
.adj-row  { display: flex; flex-wrap: wrap; gap: 3px; }
.adj-badge { display: inline-flex; align-items: center; gap: 2px; padding: 2px 6px; border-radius: 4px; font-size: .71rem; font-weight: 600; white-space: nowrap; }
.adj-pos  { background: #dcfce7; color: #166534; }
.adj-neg  { background: #fee2e2; color: #991b1b; }
.adj-zero { background: #f3f4f6; color: #9ca3af; }
.adj-none { color: #d1d5db; font-size: .78rem; }

/* ── DMI cell ────────────────────────────────────────────────── */
.dmi-cell { display: flex; flex-direction: column; gap: 2px; }
.dmi-num  { font-weight: 600; font-size: .88rem; }
.dmi-bar  { width: 44px; height: 4px; background: #e5e7eb; border-radius: 3px; overflow: hidden; }
.dmi-fill { height: 100%; background: linear-gradient(90deg, #fbbf24 0%, #34d399 100%); border-radius: 3px; }

/* ── Data cell ───────────────────────────────────────────────── */
.data-cell { display: flex; flex-direction: column; gap: 1px; }
.data-row  { font-size: .78rem; color: #6b7280; display: flex; gap: 4px; align-items: center; }
.data-dot  { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }
.dot-open      { background: #10b981; }
.dot-postponed { background: #f97316; }
.dot-closed    { background: #94a3b8; }
.dot-forfeited { background: #f59e0b; }
.dot-tx        { background: #60a5fa; }
.overdue-badge  { display: inline-flex; align-items: center; gap: 3px; padding: 2px 7px; border-radius: 4px; font-size: .71rem; font-weight: 700; background: #fee2e2; color: #b91c1c; white-space: nowrap; }
.overdue-detail { font-size: .72rem; color: #b91c1c; padding-left: 2px; }

/* ── Load-more row ───────────────────────────────────────────── */
.load-more-row { display: flex; align-items: center; justify-content: center; gap: 10px; padding: 18px; font-size: .85rem; color: #6b7280; }
</style>
